import React, { useState, useRef, useEffect, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import useSuppliers from '@/hooks/useSuppliers';
import { getSupplierDoc } from '@/services/firebaseApi';
import { ElementPosition } from '@/types';
import { ReactComponent as SearchIcon } from "@/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@/assets/icons/close.svg";
import TableList from '@/components/list/tableList/tableList';
import TableListRow from '@/components/list/tableList/tableListRow';
import TableListCell from '@/components/list/tableList/tableListCell';
import Callout from '@/components/callout/callout';
import SearchResultHolder from './SearchResultHolder';
import { ReactComponent as AddIcon } from '@/assets/icons/add.svg';
import useLayout from '@/hooks/useLayout';

interface SupplierSelectorProps {
    storageId?: string;
    className?: string;
    selectedSupplierId?: string;
    onSupplierSelect?: (supplierId: string) => void;
    placeholder?: string;
}

const SupplierSelector: FC<SupplierSelectorProps> = ({ 
    storageId,
    className, 
    selectedSupplierId,
    onSupplierSelect,
    placeholder
}) => {
    const { t } = useTranslation();
    const { openDialog } = useLayout();

    const [inFocus, setInFocus] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const SupplierSelectorWrapperRef = useRef<HTMLDivElement>(null);
    const SupplierSelectorResultsRef = useRef<HTMLDivElement>(null);
    const [inputLocation, setInputLocation] = useState<ElementPosition | null>(null);

    const {
        filteredSuppliers,
        filterQuery,
        setFilterQuery,
        suppliers
    } = useSuppliers({ storageId });

    // Get selected supplier for display
    const selectedFromList = selectedSupplierId 
        ? suppliers?.find(supplier => supplier.id === selectedSupplierId) 
        : undefined;
    const { data: selectedFromDoc } = useQuery({
        queryKey: ['supplier', storageId, selectedSupplierId],
        queryFn: () => getSupplierDoc(storageId!, selectedSupplierId!),
        enabled: !!(storageId && selectedSupplierId) && !selectedFromList,
        staleTime: 5 * 60 * 1000,
    });
    const selectedSupplier = selectedFromList || selectedFromDoc;

    const handleOnClick = () => {
        setInFocus(true);
        if (SupplierSelectorWrapperRef.current) {
            const rect = SupplierSelectorWrapperRef.current.getBoundingClientRect();
            setInputLocation({
                top: rect.bottom,
                left: rect.left,
                width: rect.width
            });
        }
    };

    const handleOnBlur = () => {
        setInFocus(false);
    };

    const handleSearchIconClick = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const location = useLocation();

    useEffect(() => {
        handleOnBlur();
        // eslint-disable-next-line
    }, [location]);

    // Update rect on window resize if inFocus
    useEffect(() => {
        if (!inFocus) return;
        const updateRect = () => {
            if (SupplierSelectorWrapperRef.current) {
                const rect = SupplierSelectorWrapperRef.current.getBoundingClientRect();
                setInputLocation({
                    top: rect.bottom,
                    left: rect.left,
                    width: rect.width
                });
            }
        };
        window.addEventListener('resize', updateRect);
        updateRect();
        return () => window.removeEventListener('resize', updateRect);
    }, [inFocus]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                SupplierSelectorWrapperRef.current && !SupplierSelectorWrapperRef.current.contains(target) &&
                SupplierSelectorResultsRef.current && !SupplierSelectorResultsRef.current.contains(target)
            ) {
                handleOnBlur();
            }
        };

        if (inFocus) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [inFocus]);

    const handleCreateSupplier = () => {
        if (!storageId) return;
        handleOnBlur();
        openDialog('create_supplier', {
            storageId,
            onCreated: (createdId: string) => {
                onSupplierSelect && onSupplierSelect(createdId);
            }
        });
    };

    const createButton = storageId ? (
        <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center justify-center p-0.5 rounded hover:bg-gray-100"
            onClick={handleCreateSupplier}
            aria-label={t('buttonCreateSupplier')}
        >
            <AddIcon className="h-5 w-5 fill-gray-500" />
        </button>
    ) : null;

    const selectedItemPreview = selectedSupplierId ? (
        <div className="relative p-3 pr-10 bg-gray-50 border border-gray-300 rounded-md flex items-center justify-between">
            <div>
                <div className="font-medium">{selectedSupplier ? `${selectedSupplier.supplierId}: ${selectedSupplier.name}` : '...'}</div>
                {selectedSupplier?.contactInformation?.email && (
                    <div className="text-sm text-gray-600">{selectedSupplier.contactInformation.email}</div>
                )}
                {selectedSupplier?.contactInformation?.phone && (
                    <div className="text-sm text-gray-600">{selectedSupplier.contactInformation.phone}</div>
                )}
            </div>
            <CloseIcon 
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer fill-gray-500 hover:fill-black"
                onClick={() => onSupplierSelect && onSupplierSelect('')}
            />
        </div>
    ) : null;

    const supplierSelector = selectedSupplierId ? null : (
        <div ref={SupplierSelectorWrapperRef} className={`relative ${className ? className : ''}`}>
            <div className="relative w-full">
                <SearchIcon 
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${storageId ? 'cursor-pointer' : 'cursor-not-allowed'} fill-gray-500`}
                    onClick={handleSearchIconClick}
                />
                <input 
                    type="text"
                    placeholder={inFocus ? (placeholder || t('placeholderSearchSupplier', 'Search suppliers...')) : ''}
                    className={`
                        rounded-md
                        w-full
                        focus:outline-none
                        pl-10
                        ${storageId ? (inFocus && filterQuery.length > 0 ? 'pr-16' : 'pr-10') : 'pr-10'}
                        ${inFocus ? 'border-gray-500 border-b-white rounded-b-none' : ''}
                    `} 
                    value={inFocus ? filterQuery : ''}
                    onChange={(e) => setFilterQuery(e.target.value)}
                    data-ignore="true"
                    onFocus={handleOnClick}
                    ref={searchInputRef}
                    disabled={!storageId}
                />
                {inFocus && filterQuery.length > 0 && (
                    <CloseIcon 
                        className={`absolute top-1/2 transform -translate-y-1/2 cursor-pointer fill-black ${storageId ? 'right-9' : 'right-3'}`} 
                        onClick={() => setFilterQuery('')}
                    />
                )}
                {createButton}
            </div>
        </div>
    );

    const results = selectedSupplierId ? null : inFocus && inputLocation && (
        <SearchResultHolder
            inputRef={inputLocation}
            resultsRef={SupplierSelectorResultsRef}
        >
            {filterQuery.length > 0 && !filteredSuppliers.length ? (
                <Callout type="error">{t('noResultsFound')}</Callout>
            ) : null}

            {filteredSuppliers.length > 0 ? (
                <TableList className="small-list hover-effect border-none rounded-t-none">
                    <TableListRow isHeader>
                        <TableListCell>{t('titleId', 'ID')}</TableListCell>
                        <TableListCell>{t('titleName', 'Name')}</TableListCell>
                        <TableListCell>{t('titleEmail', 'Email')}</TableListCell>
                        <TableListCell>{t('titlePhone', 'Phone')}</TableListCell>
                    </TableListRow>
                    {filteredSuppliers.map((supplier) => (
                        <TableListRow
                            key={supplier.id}
                            onClick={() => {
                                onSupplierSelect && onSupplierSelect(supplier.id as string);
                                handleOnBlur();
                            }}
                            className="cursor-pointer"
                        >
                            <TableListCell>{supplier.supplierId}</TableListCell>
                            <TableListCell>{supplier.name}</TableListCell>
                            <TableListCell>{supplier.contactInformation?.email || '-'}</TableListCell>
                            <TableListCell>{supplier.contactInformation?.phone || '-'}</TableListCell>
                        </TableListRow>
                    ))}
                </TableList>
            ) : null}
        </SearchResultHolder>
    );

    return (
        <>
            {supplierSelector}
            {results}
            {selectedItemPreview}
        </>
    );
};

export default SupplierSelector;
